import { Dispatch, MutableRefObject } from 'react';
import { FeatureAccessWithOptionalSubfeatures } from '../models/role-management.model';
import {
    AccessActions,
    FeatureActions as FeatureActionsEnum,
    RoleField
} from '../enums/admin-role.enum';
import { AppDispatch } from 'common/redux/core/store.config';
import { appStateActions } from 'common/redux/app-state';
import { FAILURE_HEADER } from 'common/config';
import { UNCHECK_ERROR } from '../config';
import { MessageType } from 'common/enums';
import { GridApi } from 'ag-grid-community/dist/lib/gridApi';
import { removeValidationError } from 'common/utils/error';
import { FieldError } from 'common/models';
/**This function will gives the array of top nodes of subfeatures
 * this array will be used to show the top nodes in the tree
 */
export const getFlatRows = (
    datax: Array<FeatureAccessWithOptionalSubfeatures>,
    levelxyz: number,
    applicationFeatureIdFromParent: string[],
    flattenedData: MutableRefObject<FeatureAccessWithOptionalSubfeatures[]>
): FeatureAccessWithOptionalSubfeatures[] => {
    return datax.map((ele) => {
        const featureFromSelected = flattenedData.current.find(
            (item) => item.applicationFeatureId === ele.applicationFeatureId
        );
        const newElement: FeatureAccessWithOptionalSubfeatures = {
            ...ele,
            isExpanded: ele.isExpanded ?? false,
            levelxyz,
            hasChild: ele.subFeatures.length > 0,
            parentIdArray: applicationFeatureIdFromParent,
            /** suppose if some changes made in feature and somehow it got collapsed; now when user is
             * again trying to expand it, it should show the changes made by user instead of default changes coming from backend
             * so using the feature from selected array
             */
            readAccess: featureFromSelected?.readAccess ?? ele.readAccess,
            writeAccess: featureFromSelected?.writeAccess ?? ele.writeAccess
        };
        return newElement;
    }) as unknown as FeatureAccessWithOptionalSubfeatures[];
};
const getFlattenedRows = (
    data: FeatureAccessWithOptionalSubfeatures[],
    levelxyz: number,
    parentIdArray: string[],
    actionName: AccessActions,
    value: boolean
): FeatureAccessWithOptionalSubfeatures[] => {
    return data
        .map((ele) => {
            /**if actionName === accessActions.Read and writeAccess is true execution will not reach here
             * it will be handled in checkboxHandler function condition 1
             * if actionName === accessActions.Write  &&  value === true  then if readAccess is false we need to check readAccess as well
             * else if actionName === accessActions.Write  &&  value === false  then keep reasAccess as it is
             * else i.e  actionName === accessActions.Read  that means ele.writeAccess is always false (because of 1st line of this comment)
             *  then keep writeAccess as it is and assign value to readAccess
             */
            let newElement: FeatureAccessWithOptionalSubfeatures;
            if (actionName === AccessActions.WRITE && value === true) {
                newElement = {
                    ...ele,
                    readAccess: true,
                    writeAccess: true,
                    isExpanded: ele.subFeatures.length > 0,
                    levelxyz,
                    hasChild: ele.subFeatures.length > 0,
                    parentIdArray: parentIdArray
                };
            } else if (actionName === AccessActions.WRITE && value === false) {
                newElement = {
                    ...ele,
                    writeAccess: false,
                    isExpanded: ele.subFeatures.length > 0,
                    levelxyz,
                    hasChild: ele.subFeatures.length > 0,
                    parentIdArray: parentIdArray
                };
            } else if (actionName === AccessActions.READ) {
                newElement = {
                    ...ele,
                    readAccess: value,
                    isExpanded: ele.subFeatures.length > 0,
                    levelxyz,
                    hasChild: ele.subFeatures.length > 0,
                    parentIdArray: parentIdArray
                };
            } else {
                newElement = {
                    ...ele,
                    isExpanded: ele.subFeatures.length > 0,
                    levelxyz,
                    hasChild: ele.subFeatures.length > 0,
                    parentIdArray: parentIdArray
                };
            }
            if (ele.subFeatures.length > 0) {
                return [
                    newElement,
                    ...getFlattenedRows(
                        ele.subFeatures,
                        levelxyz + 1,
                        [...parentIdArray, ele.applicationFeatureId],
                        actionName,
                        value
                    )
                ];
            }
            return newElement;
        })
        .flat(Infinity) as unknown as FeatureAccessWithOptionalSubfeatures[];
};

function updateSubFeatures(obj: any) {
    if (obj.subFeatures) {
        obj.subFeatures.forEach((subFeature: any) => {
            subFeature.readAccess = obj.readAccess;
            subFeature.writeAccess = obj.writeAccess;

            updateSubFeatures(subFeature);
        });
    }

    return obj;
}

export const markAllChildren = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newData: any,
    value: boolean,
    actionName: AccessActions,
    flattenedData: MutableRefObject<FeatureAccessWithOptionalSubfeatures[]>,
    setAllFeaturesData: Dispatch<React.SetStateAction<FeatureAccessWithOptionalSubfeatures[]>>
) => {
    const indexOfNewData = flattenedData.current.findIndex(
        (f) => f.applicationFeatureId === newData.applicationFeatureId
    );
    flattenedData.current.splice(indexOfNewData, 1, newData);

    const newCopyObj = { ...newData };
    const updatedObj = updateSubFeatures(newCopyObj);

    const flattenedRows = getFlattenedRows(
        updatedObj.subFeatures,
        updatedObj.levelxyz + 1,
        [...updatedObj.parentIdArray, updatedObj.applicationFeatureId],
        actionName,
        value
    );
    flattenedRows.forEach((ele) => {
        const index = flattenedData.current.findIndex(
            (f) => f.applicationFeatureId === ele.applicationFeatureId
        );

        flattenedData.current.splice(index, 1, ele);
    });
    setAllFeaturesData((p) => {
        return p.map((e) => {
            if (e.applicationFeatureId === data.applicationFeatureId) {
                return newData;
            } else if (e?.parentIdArray && e?.parentIdArray.includes(data.applicationFeatureId)) {
                if (actionName === AccessActions.WRITE && value === true) {
                    return {
                        ...e,
                        readAccess: true,
                        writeAccess: true
                    };
                } else if (actionName === AccessActions.WRITE && value === false) {
                    return {
                        ...e,
                        writeAccess: false
                    };
                } else if (actionName === AccessActions.READ) {
                    return {
                        ...e,
                        readAccess: value
                    };
                } else {
                    return e;
                }
            }
            return e;
        });
    });
};

export const markAllParents = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newData: any,
    value: boolean,
    actionName: AccessActions,
    flattenedData: MutableRefObject<FeatureAccessWithOptionalSubfeatures[]>,
    setAllFeaturesData: Dispatch<React.SetStateAction<FeatureAccessWithOptionalSubfeatures[]>>
) => {
    /** traverse through the prentsIdArray check if each parents all children are checked or not
     *  if checked
     * then mark it as true
     * else false
     *
     * flattenedData.current contains flat data converted from tree we are keeping track of parents in parentIdArray
     * this is how we can find childrens of a particular parent
     *
     * please note that this flattened data will be used as a payload to save or update role
     *  */
    const parentsArray: FeatureAccessWithOptionalSubfeatures[] = [];
    if (actionName === AccessActions.READ) {
        newData.readAccess = value;
    } else if (actionName === AccessActions.WRITE) {
        if (value === true) {
            newData.readAccess = true;
            newData.writeAccess = true;
        } else {
            newData.writeAccess = false;
        }
    }
    const indexOfNewData = flattenedData.current.findIndex(
        (f) => f.applicationFeatureId === newData.applicationFeatureId
    );
    flattenedData.current.splice(indexOfNewData, 1, newData);

    data?.parentIdArray?.reverse().forEach((ele: string) => {
        const parent = flattenedData.current.find((e) => e.applicationFeatureId === ele);
        if (parent) {
            parent.readAccess = flattenedData.current
                .filter((e) => e.parentIdArray?.includes(ele))
                .every((e) => {
                    if (e.parentApplicationFeatureId === data.applicationFaeatureId) {
                        return newData.readAccess === true;
                    } else {
                        return e.readAccess === true;
                    }
                });
            parent.writeAccess = flattenedData.current
                .filter((e) => e.parentIdArray?.includes(ele))
                .every((e) => {
                    if (e.parentApplicationFeatureId === data.applicationFaeatureId) {
                        return newData.writeAccess === true;
                    } else {
                        return e.writeAccess === true;
                    }
                });
            parentsArray.push(parent);
        }
    });
    parentsArray.forEach((ele) => {
        const index = flattenedData.current.findIndex(
            (f) => f.applicationFeatureId === ele.applicationFeatureId
        );
        flattenedData.current.splice(index, 1, ele);
    });
    setAllFeaturesData((p) => {
        const newArray = p.map((e) => {
            const featureFromSelected = flattenedData.current.find(
                (feature) => feature.applicationFeatureId === e.applicationFeatureId
            );
            if (featureFromSelected) {
                return {
                    ...e,
                    readAccess: featureFromSelected.readAccess,
                    writeAccess: featureFromSelected.writeAccess
                };
            }
            return e;
        });
        return newArray;
    });
};

export const onCollapse = (
    value: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    action: FeatureActionsEnum,
    api: GridApi,
    rowIndex: number,
    setAllFeaturesData: Dispatch<React.SetStateAction<FeatureAccessWithOptionalSubfeatures[]>>
) => {
    const newData = { ...data };
    newData.isExpanded = !newData.isExpanded;
    setAllFeaturesData((p) => {
        const newArray = p.filter((e) =>
            e.parentIdArray ? !e.parentIdArray.includes(data.applicationFeatureId) : false
        );
        newArray.splice(rowIndex, 1, newData);
        return newArray;
    });
};
const onExpansionToggle = (
    value: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    action: FeatureActionsEnum,
    api: GridApi,
    rowIndex: number,
    setAllFeaturesData: Dispatch<React.SetStateAction<FeatureAccessWithOptionalSubfeatures[]>>,
    flattenedData: MutableRefObject<FeatureAccessWithOptionalSubfeatures[]>
) => {
    const addIndex = rowIndex + 1;
    const newData = { ...data };
    const newRows =
        data.subFeatures.length !== 0
            ? getFlatRows(
                  data.subFeatures,
                  data.levelxyz + 1,
                  [...data.parentIdArray, data.applicationFeatureId],
                  flattenedData
              )
            : [];
    newData.isExpanded = !newData.isExpanded;
    return setAllFeaturesData((p) => {
        const f = p.slice(0, rowIndex);
        const s = p.slice(addIndex, p.length);
        return [...f, newData, ...newRows, ...s];
    });
};

function updateObject(obj: any, dataArray: any) {
    if (dataArray.some((data: any) => data.appId === obj.appId)) {
        // Update the read and write properties of the obj
        const updatedData = dataArray.find((data: any) => data.appId === obj.appId);
        obj.readAccess = updatedData.readAccess;
        obj.writeAccess = updatedData.writeAccess;
    }

    if (obj.subFeatures) {
        // Recursively update subFeatures
        obj.subFeatures = obj.subFeatures.map((subFeature: any) =>
            updateObject(subFeature, dataArray)
        );
    }

    return obj;
}

const checkboxHandler = (
    value: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    action: FeatureActionsEnum,
    api: GridApi,
    rowIndex: number,
    actionName: AccessActions,
    setAllFeaturesData: Dispatch<React.SetStateAction<FeatureAccessWithOptionalSubfeatures[]>>,
    dispatch: AppDispatch,
    flattenedData: MutableRefObject<FeatureAccessWithOptionalSubfeatures[]>
) => {
    const newData = { ...data };
    if (
        actionName === AccessActions.READ &&
        data.writeAccess === true &&
        data.readAccess === true &&
        value === false
    ) {
        /**condition 1 : user unchecking read Access while write is already checked */
        dispatch({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: UNCHECK_ERROR,
                type: MessageType.Error
            }
        });
        return;
    } else if (
        actionName === AccessActions.READ &&
        data.readAccess === false &&
        data.writeAccess === true &&
        value === true
    ) {
        /**condition 2 : this condition will never come but adding it
         * if write access is true and read access false and user checking read access to true
         */
        newData.readAccess = value;
    } else if (actionName === AccessActions.READ && data.writeAccess === false) {
        /** condition 2 : user checking or unchecking when write is not checked , directly assign true or false to read access */
        newData.readAccess = value;
    } else if (actionName === AccessActions.WRITE && data.readAccess === false && value === true) {
        /** condition 3 : user trying to check write while read is unchecked in that case check read access as well */
        newData.writeAccess = true;
        newData.readAccess = true;
    } else if (actionName === AccessActions.WRITE && data.readAccess === true) {
        /** condition 4 : user trying to check or uncheck while read is true , direactly assign write acces true or false  */
        newData.writeAccess = value;
    }
    /**
     * please note that on save of create new role or edit role we need to send all data irrespective of old data
     * on db side complete old data will be deleted and new records will be inserted so we need to send complete set of data in an array
     * we have created two methods 1.markAllParents 2. markAllChildren
     * 1.markAllParents : to mark parents in case when middle node or bottom node is checked or unchecked
     * 2.markAllChildren : to mark children in case when top node or middle node is checked or unchecked
     */
    if (data.subFeatures.length === 0 && data.parentApplicationFeatureId === null) {
        /** has no parent and no child  ==> top node without children in tree */
        if (actionName === AccessActions.READ) newData.readAccess = value;
        else if (actionName === AccessActions.WRITE) {
            if (value) {
                newData.readAccess = value;
            }
            newData.writeAccess = value;
        }
        const index = flattenedData.current.findIndex(
            (ele) => ele.applicationFeatureId === data.applicationFeatureId
        );
        flattenedData.current.splice(index, 1, newData);
        setAllFeaturesData((p) => {
            const newArray = p.map((e) => {
                if (e.applicationFeatureId === data.applicationFeatureId) {
                    return newData;
                }
                return e;
            });
            return newArray;
        });
    } else if (data.subFeatures.length !== 0 && data.parentApplicationFeatureId === null) {
        /** has no parent but has children ==> top node with children  */
        markAllChildren(data, newData, value, actionName, flattenedData, setAllFeaturesData);
    } else if (data.subFeatures.length !== 0 && data.parentApplicationFeatureId !== null) {
        /** has parent and  children ==> middle node with parent and children*/
        markAllChildren(data, newData, value, actionName, flattenedData, setAllFeaturesData);
        markAllParents(data, newData, value, actionName, flattenedData, setAllFeaturesData);
    } else if (data.subFeatures.length === 0 && data.parentApplicationFeatureId !== null) {
        /** has parent and no children ==> bottom node with parent and no children*/

        markAllParents(data, newData, value, actionName, flattenedData, setAllFeaturesData);
    }
};
export const onAction = (
    value: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    action: FeatureActionsEnum,
    api: GridApi,
    rowIndex: number,
    actionName: AccessActions,
    setAllFeaturesData: Dispatch<React.SetStateAction<FeatureAccessWithOptionalSubfeatures[]>>,
    dispatch: AppDispatch,
    flattenedData: MutableRefObject<FeatureAccessWithOptionalSubfeatures[]>,
    setFieldErrors: Dispatch<React.SetStateAction<Array<FieldError>>>,
    fieldErrors: Array<FieldError>
) => {
    switch (action) {
        case FeatureActionsEnum.COLLAPSE:
            onCollapse(value, data, action, api, rowIndex, setAllFeaturesData);
            break;
        case FeatureActionsEnum.Expand:
            onExpansionToggle(
                value,
                data,
                action,
                api,
                rowIndex,
                setAllFeaturesData,
                flattenedData
            );
            break;
        case FeatureActionsEnum.CHECK_UNCHECK:
            {
                checkboxHandler(
                    value,
                    data,
                    action,
                    api,
                    rowIndex,
                    actionName,
                    setAllFeaturesData,
                    dispatch,
                    flattenedData
                );
                const errors = removeValidationError(fieldErrors, RoleField.WRITE_WITHOUT_READ);
                setFieldErrors(errors);
            }
            break;

        default:
            break;
    }
};
